import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Fab,
  Checkbox,
  FormGroup,
  InputAdornment,
  Badge,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  CheckCircle,
  Error,
  Warning,
  Info,
  Speed,
  Link,
  Settings,
  Visibility,
  Search,
  FilterList,
  ExpandMore,
  PlayArrow,
  Pause,
  Stop,
  History,
  TrendingUp,
  TrendingDown,
  NetworkCheck,
  Security,
  Schedule,
  Notifications,
  Download,
  Upload,
  MoreVert,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { endpointsAPI } from '../services/api';

// Enhanced styled components
const StatusChip = styled(Chip)(({ theme, status }) => ({
  backgroundColor: 
    status === 'healthy' ? theme.palette.success.light :
    status === 'warning' ? theme.palette.warning.light :
    status === 'error' ? theme.palette.error.light :
    theme.palette.grey.light,
  color: 
    status === 'healthy' ? theme.palette.success.contrastText :
    status === 'warning' ? theme.palette.warning.contrastText :
    status === 'error' ? theme.palette.error.contrastText :
    theme.palette.grey.contrastText,
  fontWeight: 600,
}));

const MetricCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  '& .MuiCardContent-root': {
    padding: theme.spacing(2),
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  padding: '8px 16px',
}));

const EndpointManagement = () => {
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    type: 'internal',
    description: '',
    healthCheck: true,
    timeout: 5000,
    retryCount: 3,
    priority: 'medium',
  });
  const [testingEndpoint, setTestingEndpoint] = useState(null);
  const [selectedEndpoints, setSelectedEndpoints] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [monitoringData, setMonitoringData] = useState({});

  useEffect(() => {
    fetchEndpoints();
    fetchMonitoringData();
  }, []);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchEndpoints();
        fetchMonitoringData();
      }, 30000); // Refresh every 30 seconds
    }
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const fetchEndpoints = async () => {
    try {
      setLoading(true);
      const data = await endpointsAPI.getAll();
      setEndpoints(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch endpoints');
      console.error('Endpoints fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonitoringData = async () => {
    try {
      const data = await endpointsAPI.getStatus();
      setMonitoringData(data);
    } catch (err) {
      console.error('Monitoring data fetch error:', err);
    }
  };

  const handleOpenDialog = (endpoint = null) => {
    if (endpoint) {
      setEditingEndpoint(endpoint);
      setFormData({
        name: endpoint.name,
        url: endpoint.url,
        type: endpoint.type,
        description: endpoint.description || '',
        healthCheck: endpoint.healthCheck,
        timeout: endpoint.timeout || 5000,
        retryCount: endpoint.retryCount || 3,
        priority: endpoint.priority || 'medium',
      });
    } else {
      setEditingEndpoint(null);
      setFormData({
        name: '',
        url: '',
        type: 'internal',
        description: '',
        healthCheck: true,
        timeout: 5000,
        retryCount: 3,
        priority: 'medium',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEndpoint(null);
    setFormData({
      name: '',
      url: '',
      type: 'internal',
      description: '',
      healthCheck: true,
      timeout: 5000,
      retryCount: 3,
      priority: 'medium',
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingEndpoint) {
        await endpointsAPI.update(editingEndpoint.id, formData);
        setSuccess('Endpoint updated successfully');
      } else {
        await endpointsAPI.create(formData);
        setSuccess('Endpoint created successfully');
      }
      await fetchEndpoints();
      handleCloseDialog();
    } catch (err) {
      setError('Failed to save endpoint');
      console.error('Endpoint save error:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this endpoint?')) {
      try {
        await endpointsAPI.delete(id);
        setSuccess('Endpoint deleted successfully');
        await fetchEndpoints();
      } catch (err) {
        setError('Failed to delete endpoint');
        console.error('Endpoint delete error:', err);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${selectedEndpoints.length} endpoints?`)) {
      try {
        for (const id of selectedEndpoints) {
          await endpointsAPI.delete(id);
        }
        setSuccess(`${selectedEndpoints.length} endpoints deleted successfully`);
        setSelectedEndpoints([]);
        await fetchEndpoints();
      } catch (err) {
        setError('Failed to delete endpoints');
        console.error('Bulk delete error:', err);
      }
    }
  };

  const handleTestConnection = async (id) => {
    try {
      setTestingEndpoint(id);
      const result = await endpointsAPI.testConnection(id);
      const updatedEndpoints = endpoints.map(ep => 
        ep.id === id 
          ? { ...ep, status: result.status, responseTime: result.responseTime, lastChecked: new Date().toISOString() }
          : ep
      );
      setEndpoints(updatedEndpoints);
      setSuccess('Connection test completed');
    } catch (err) {
      setError('Failed to test connection');
      console.error('Connection test error:', err);
    } finally {
      setTestingEndpoint(null);
    }
  };

  const handleBulkTest = async () => {
    try {
      setTestingEndpoint('bulk');
      for (const id of selectedEndpoints) {
        await endpointsAPI.testConnection(id);
      }
      await fetchEndpoints();
      setSuccess('Bulk connection test completed');
    } catch (err) {
      setError('Failed to test connections');
      console.error('Bulk test error:', err);
    } finally {
      setTestingEndpoint(null);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedEndpoints(filteredEndpoints.map(ep => ep.id));
    } else {
      setSelectedEndpoints([]);
    }
  };

  const handleSelectEndpoint = (id, checked) => {
    if (checked) {
      setSelectedEndpoints([...selectedEndpoints, id]);
    } else {
      setSelectedEndpoints(selectedEndpoints.filter(epId => epId !== id));
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle color="success" />;
      case 'warning': return <Warning color="warning" />;
      case 'error': return <Error color="error" />;
      default: return <Info color="info" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getResponseTimeColor = (responseTime) => {
    if (responseTime < 100) return 'success';
    if (responseTime < 500) return 'warning';
    return 'error';
  };

  // Filter endpoints based on search and filters
  const filteredEndpoints = endpoints.filter(endpoint => {
    const matchesSearch = endpoint.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         endpoint.url.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || endpoint.type === filterType;
    const matchesStatus = filterStatus === 'all' || endpoint.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Endpoint Management
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage and monitor your API endpoints with real-time health checks
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Auto Refresh">
            <IconButton 
              onClick={() => setAutoRefresh(!autoRefresh)} 
              color={autoRefresh ? 'primary' : 'default'}
            >
              <Badge color="primary" variant="dot" invisible={!autoRefresh}>
                <Refresh />
              </Badge>
            </IconButton>
          </Tooltip>
          <ActionButton
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add Endpoint
          </ActionButton>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Enhanced Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4">{endpoints.length}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Total Endpoints
                  </Typography>
                </Box>
                <Link sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </MetricCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" color="success.main">
                    {endpoints.filter(ep => ep.status === 'healthy').length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Healthy
                  </Typography>
                </Box>
                <CheckCircle color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" color="warning.main">
                    {endpoints.filter(ep => ep.status === 'warning').length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Warning
                  </Typography>
                </Box>
                <Warning color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" color="error.main">
                    {endpoints.filter(ep => ep.status === 'error').length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Error
                  </Typography>
                </Box>
                <Error color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search endpoints..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  label="Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="internal">Internal</MenuItem>
                  <MenuItem value="external">External</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="healthy">Healthy</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                  <MenuItem value="error">Error</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" gap={1}>
                {selectedEndpoints.length > 0 && (
                  <>
                    <ActionButton
                      variant="outlined"
                      startIcon={<PlayArrow />}
                      onClick={handleBulkTest}
                      disabled={testingEndpoint === 'bulk'}
                    >
                      Test All ({selectedEndpoints.length})
                    </ActionButton>
                    <ActionButton
                      variant="outlined"
                      color="error"
                      startIcon={<Delete />}
                      onClick={handleBulkDelete}
                    >
                      Delete ({selectedEndpoints.length})
                    </ActionButton>
                  </>
                )}
                <ActionButton
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchEndpoints}
                >
                  Refresh
                </ActionButton>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Enhanced Endpoints Table */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Endpoints ({filteredEndpoints.length})
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedEndpoints.length === filteredEndpoints.length && filteredEndpoints.length > 0}
                    indeterminate={selectedEndpoints.length > 0 && selectedEndpoints.length < filteredEndpoints.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                }
                label="Select All"
              />
            </Box>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox"></TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Response Time</TableCell>
                  <TableCell>Last Checked</TableCell>
                  <TableCell>Health Check</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEndpoints.map((endpoint) => (
                  <TableRow key={endpoint.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedEndpoints.includes(endpoint.id)}
                        onChange={(e) => handleSelectEndpoint(endpoint.id, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {getStatusIcon(endpoint.status)}
                        <Box sx={{ ml: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {endpoint.name}
                          </Typography>
                          {endpoint.description && (
                            <Typography variant="caption" color="textSecondary">
                              {endpoint.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {endpoint.url}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={endpoint.type}
                        size="small"
                        color={endpoint.type === 'internal' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={endpoint.priority || 'medium'}
                        size="small"
                        color={getPriorityColor(endpoint.priority || 'medium')}
                      />
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        label={endpoint.status}
                        size="small"
                        status={endpoint.status}
                      />
                    </TableCell>
                    <TableCell>
                      {endpoint.responseTime ? (
                        <Box display="flex" alignItems="center">
                          <Speed sx={{ fontSize: 16, mr: 0.5 }} />
                          <Typography 
                            variant="body2" 
                            color={`${getResponseTimeColor(endpoint.responseTime)}.main`}
                          >
                            {endpoint.responseTime}ms
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {endpoint.lastChecked 
                          ? new Date(endpoint.lastChecked).toLocaleString()
                          : 'Never'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={endpoint.healthCheck}
                        size="small"
                        disabled
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex">
                        <Tooltip title="Test Connection">
                          <IconButton
                            size="small"
                            onClick={() => handleTestConnection(endpoint.id)}
                            disabled={testingEndpoint === endpoint.id}
                          >
                            {testingEndpoint === endpoint.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(endpoint)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(endpoint.id)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Enhanced Add/Edit Endpoint Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingEndpoint ? 'Edit Endpoint' : 'Add New Endpoint'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    label="Type"
                  >
                    <MenuItem value="internal">Internal</MenuItem>
                    <MenuItem value="external">External</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="URL"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  margin="normal"
                  required
                  placeholder="https://example.com/api"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  margin="normal"
                  multiline
                  rows={3}
                />
              </Grid>
              
              {/* Advanced Settings */}
              <Grid item xs={12}>
                <Accordion expanded={showAdvanced} onChange={() => setShowAdvanced(!showAdvanced)}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">Advanced Settings</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Timeout (ms)"
                          type="number"
                          value={formData.timeout}
                          onChange={(e) => setFormData({ ...formData, timeout: parseInt(e.target.value) })}
                          margin="normal"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Retry Count"
                          type="number"
                          value={formData.retryCount}
                          onChange={(e) => setFormData({ ...formData, retryCount: parseInt(e.target.value) })}
                          margin="normal"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth margin="normal">
                          <InputLabel>Priority</InputLabel>
                          <Select
                            value={formData.priority}
                            onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                            label="Priority"
                          >
                            <MenuItem value="low">Low</MenuItem>
                            <MenuItem value="medium">Medium</MenuItem>
                            <MenuItem value="high">High</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.healthCheck}
                      onChange={(e) => setFormData({ ...formData, healthCheck: e.target.checked })}
                    />
                  }
                  label="Enable Health Check"
                  sx={{ mt: 2 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingEndpoint ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EndpointManagement;